import {
  SERVICE_TICKET_STATUSES,
  type ServiceTicket,
  type ServiceTicketDetail,
  type ServiceTicketStatus,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  formatDateTime,
  Loading,
  PageHeader,
  returnToPrevious,
  ServiceThread,
  type ServiceThreadWords,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  keys,
  platformApi,
  tenantQuery,
  tenantServiceQuery,
  tenantServiceTicketQuery,
} from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { ScanNowButton, TicketBadges } from '../components/ServiceTickets';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { fixScreen, ticketExplanation, ticketTitle } from '../lib/service';

const ROUTE = '/app/tenants/$tenantId/service';

/**
 * One store's faults and service calls (adr/0036): the list on one side, the chosen ticket with
 * its conversation on the other. The platform staff answer the store, move a ticket between
 * open, in progress and resolved, and can scan the store on demand.
 */
export function TenantServicePage() {
  const { tenantId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: '/tenants/$tenantId/service' });
  const session = useQuery(sessionQuery);
  const canManage =
    session.data !== undefined && hasCapability(session.data, 'platform.support.manage');
  const tenant = useQuery(tenantQuery(tenantId));
  const list = useQuery(tenantServiceQuery(tenantId));
  const showAll = search.all === true;
  const tickets =
    list.data?.tickets.filter((ticket) => showAll || ticket.status !== 'RESOLVED') ?? [];
  const name = tenant.data?.tenant.displayName;
  const choose = (ticketId: string | undefined) =>
    void navigate({ search: (prev) => ({ ...prev, ticket: ticketId }), replace: true });

  return (
    <>
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.service.back}
      </Link>
      <PageHeader
        title={name === undefined ? he.service.title : `${he.service.title}: ${name}`}
        actions={canManage ? <ScanNowButton tenantId={tenantId} /> : null}
      />
      <p className="mb-4 max-w-3xl text-sm text-slate-600">{he.service.intro}</p>
      {canManage ? null : (
        <div className="mb-4">
          <Alert tone="info">{he.service.readOnly}</Alert>
        </div>
      )}
      {list.isPending ? <Loading /> : null}
      {list.isError ? (
        <Alert>{errorMessage(list.error, { NOT_FOUND: he.service.notFound })}</Alert>
      ) : null}
      {list.data === undefined ? null : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Card
            title={he.service.title}
            actions={
              <div className="flex gap-1" role="group">
                {([false, true] as const).map((all) => (
                  <Button
                    key={String(all)}
                    tone={showAll === all ? 'primary' : 'secondary'}
                    aria-pressed={showAll === all}
                    onClick={() => {
                      void navigate({
                        search: (prev) => ({ ...prev, all: all ? true : undefined }),
                        replace: true,
                      });
                    }}
                  >
                    {all ? he.service.filterAll : he.service.filterOpen}
                  </Button>
                ))}
              </div>
            }
          >
            <p className="mb-3 text-xs text-slate-500" data-testid="service-last-scan">
              {list.data.scannedAt === null
                ? he.service.neverScanned
                : `${he.service.lastScan}: ${formatDateTime(list.data.scannedAt)}`}
            </p>
            {tickets.length === 0 ? (
              <p className="text-sm text-slate-600" data-testid="service-empty">
                {list.data.tickets.length === 0 ? he.service.noneAtAll : he.service.none}
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-slate-100" data-testid="service-list">
                {tickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    selected={ticket.id === search.ticket}
                    onSelect={() => {
                      choose(ticket.id);
                    }}
                  />
                ))}
              </ul>
            )}
          </Card>
          {search.ticket === undefined ? (
            <Card>
              <p className="text-sm text-slate-600">{he.service.pick}</p>
            </Card>
          ) : (
            <TicketPanel
              key={search.ticket}
              tenantId={tenantId}
              ticketId={search.ticket}
              canManage={canManage}
            />
          )}
        </div>
      )}
    </>
  );
}

function TicketRow({
  ticket,
  selected,
  onSelect,
}: {
  ticket: ServiceTicket;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        data-testid="service-row"
        className={`flex w-full flex-col items-start gap-1 rounded-md px-2 py-3 text-start text-sm hover:bg-slate-50 ${
          selected ? 'bg-brand-50 ring-1 ring-brand-200' : ''
        }`}
      >
        <span className="font-medium text-slate-800">{ticketTitle(ticket)}</span>
        <TicketBadges ticket={ticket} />
        <span className="text-xs text-slate-500">
          {he.service.updated}: {formatDateTime(ticket.updatedAt)}
          {ticket.messageCount > 0 ? ` · ${he.service.messages(ticket.messageCount)}` : ''}
        </span>
      </button>
    </li>
  );
}

const THREAD_WORDS = (scan: boolean): ServiceThreadWords => ({
  ...he.service.reply,
  reply: scan ? he.service.reply.replyScan : he.service.reply.reply,
});

function TicketPanel({
  tenantId,
  ticketId,
  canManage,
}: {
  tenantId: string;
  ticketId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery(tenantServiceTicketQuery(tenantId, ticketId));
  const refresh = async (next: ServiceTicketDetail) => {
    queryClient.setQueryData(keys.tenantServiceTicket(tenantId, ticketId), next);
    await queryClient.invalidateQueries({ queryKey: keys.tenantServiceList(tenantId) });
    await queryClient.invalidateQueries({ queryKey: keys.serviceEverywhere });
  };
  const status = useMutation({
    mutationFn: (next: ServiceTicketStatus) =>
      platformApi.setServiceTicketStatus(tenantId, ticketId, next),
    onSuccess: refresh,
  });

  if (detail.isPending) {
    return (
      <Card>
        <Loading />
      </Card>
    );
  }
  if (detail.isError) {
    return (
      <Card>
        <Alert>{errorMessage(detail.error, { NOT_FOUND: he.service.ticketNotFound })}</Alert>
      </Card>
    );
  }
  const { ticket } = detail.data;
  const explanation = ticketExplanation(ticket);
  const scan = ticket.source === 'SCAN';
  const screen = ticket.check === null ? null : fixScreen(ticket.check);
  return (
    <Card title={ticketTitle(ticket)}>
      <div className="flex flex-col gap-4" data-testid="service-ticket">
        <TicketBadges ticket={ticket} />
        {explanation === null ? null : (
          <p className="text-sm text-slate-700" data-testid="service-explanation">
            {explanation}
          </p>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-xs text-slate-600">
          <dt>{he.service.sourceLabel}</dt>
          <dd>{he.service.sources[ticket.source]}</dd>
          <dt>{he.service.opened}</dt>
          <dd>{formatDateTime(ticket.createdAt)}</dd>
          {ticket.lastSeenAt === null ? null : (
            <>
              <dt>{he.service.lastSeen}</dt>
              <dd>{formatDateTime(ticket.lastSeenAt)}</dd>
            </>
          )}
          {ticket.resolvedAt === null ? null : (
            <>
              <dt>{he.service.resolvedAt}</dt>
              <dd>
                {formatDateTime(ticket.resolvedAt)}
                {ticket.resolvedByScan ? ` · ${he.service.resolvedByScan}` : ''}
              </dd>
            </>
          )}
        </dl>
        {screen === null ? null : <FixLink tenantId={tenantId} screen={screen} />}
        {canManage ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-slate-600">{he.service.setStatus}</span>
            <div className="flex flex-wrap gap-2" role="group">
              {SERVICE_TICKET_STATUSES.map((value) => (
                <Button
                  key={value}
                  tone={ticket.status === value ? 'primary' : 'secondary'}
                  aria-pressed={ticket.status === value}
                  busy={status.isPending && status.variables === value}
                  disabled={status.isPending}
                  data-testid={`service-status-${value}`}
                  onClick={() => {
                    if (ticket.status !== value) status.mutate(value);
                  }}
                >
                  {he.service.statuses[value]}
                </Button>
              ))}
              {status.isSuccess ? (
                <span role="status" className="self-center text-xs text-green-800">
                  {he.service.statusSaved}
                </span>
              ) : null}
            </div>
            {scan ? <p className="text-xs text-slate-500">{he.service.scanReopens}</p> : null}
            {status.isError ? <Alert>{errorMessage(status.error)}</Alert> : null}
          </div>
        ) : null}
        <ServiceThread
          detail={detail.data}
          viewer="PLATFORM"
          words={THREAD_WORDS(scan)}
          canReply={canManage}
          onReply={async (body) => {
            await refresh(await platformApi.replyServiceTicket(tenantId, ticketId, body));
          }}
          errorMessage={errorMessage}
        />
      </div>
    </Card>
  );
}

/** The store's screen where a scan finding is dealt with. */
function FixLink({ tenantId, screen }: { tenantId: string; screen: ReturnType<typeof fixScreen> }) {
  const className = 'text-sm font-medium text-brand-700 hover:underline';
  switch (screen) {
    case 'domains':
      return (
        <Link to="/tenants/$tenantId/domains" params={{ tenantId }} className={className}>
          {he.service.fix}
        </Link>
      );
    case 'contact':
      return (
        <Link to="/tenants/$tenantId/contact" params={{ tenantId }} className={className}>
          {he.service.fix}
        </Link>
      );
    case 'copy':
      return (
        <Link to="/tenants/$tenantId/copy" params={{ tenantId }} search={{}} className={className}>
          {he.service.fix}
        </Link>
      );
    case 'store':
      return (
        <Link to="/tenants/$tenantId" params={{ tenantId }} className={className}>
          {he.service.fix}
        </Link>
      );
  }
}
